<script lang="ts">
  /** Recent entries, grouped by date, with inline edit and delete. */
  import type { Entry } from '$shared/types';
  import { formatDayShort, formatHours, today } from '$shared/dates';
  import { store } from '$lib/store.svelte';

  interface Props {
    entries: Entry[];
    limit?: number;
  }

  let { entries, limit = 40 }: Props = $props();

  let editingId = $state<string | null>(null);
  let draftHours = $state(0);
  let draftNotes = $state('');
  let confirmingDelete = $state<string | null>(null);

  const groups = $derived.by(() => {
    const byDate = new Map<string, Entry[]>();
    for (const entry of entries.slice(0, limit)) {
      const list = byDate.get(entry.date);
      if (list) list.push(entry);
      else byDate.set(entry.date, [entry]);
    }
    return [...byDate.entries()].map(([date, items]) => ({
      date,
      items,
      total: items.reduce((sum, e) => sum + e.hours, 0),
    }));
  });

  function beginEdit(entry: Entry) {
    editingId = entry.id;
    draftHours = entry.hours;
    draftNotes = entry.notes;
    confirmingDelete = null;
  }

  async function commitEdit(entry: Entry) {
    if (draftHours > 0 && (draftHours !== entry.hours || draftNotes !== entry.notes)) {
      await store.editEntry(entry.id, { hours: draftHours, notes: draftNotes.trim() });
    }
    editingId = null;
  }
</script>

{#if !groups.length}
  <p class="muted empty">No entries yet. Log one above.</p>
{:else}
  <div class="list">
    {#each groups as group (group.date)}
      <section>
        <header class="spread">
          <h3>
            {formatDayShort(group.date)}
            {#if group.date === today()}<span class="badge">today</span>{/if}
          </h3>
          <span class="muted num">{formatHours(group.total)}</span>
        </header>

        {#each group.items as entry (entry.id)}
          <article class="entry" class:editing={editingId === entry.id}>
            {#if editingId === entry.id}
              <div class="edit">
                <input
                  class="num hours"
                  type="number"
                  step="0.5"
                  min="0"
                  inputmode="decimal"
                  bind:value={draftHours}
                  aria-label="Hours"
                />
                <input type="text" bind:value={draftNotes} aria-label="Notes" placeholder="Notes" />
                <div class="row">
                  <button class="ghost" onclick={() => (editingId = null)}>Cancel</button>
                  <button class="primary" onclick={() => commitEdit(entry)}>Save</button>
                </div>
              </div>
            {:else}
              <button class="tap" onclick={() => beginEdit(entry)}>
                <span class="hours num">{entry.hours}</span>
                <span class="notes">{entry.notes || '—'}</span>
                {#if store.pending.has(entry.id)}
                  <span class="pending" title="Not yet synced" aria-label="Not yet synced"></span>
                {/if}
              </button>

              {#if confirmingDelete === entry.id}
                <div class="row confirm">
                  <button class="ghost" onclick={() => (confirmingDelete = null)}>Keep</button>
                  <button class="danger" onclick={() => store.removeEntry(entry.id)}>Delete</button>
                </div>
              {:else}
                <button
                  class="ghost del"
                  onclick={() => (confirmingDelete = entry.id)}
                  aria-label="Delete entry">×</button
                >
              {/if}
            {/if}
          </article>
        {/each}
      </section>
    {/each}
  </div>
{/if}

<style>
  .empty {
    text-align: center;
    padding: 2rem 1rem;
  }

  .list {
    display: grid;
    gap: 1.1rem;
  }

  header {
    padding-bottom: 0.35rem;
    border-bottom: 1px solid var(--line);
    margin-bottom: 0.3rem;
  }

  h3 {
    font-size: 0.82rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--muted);
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .badge {
    background: var(--accent);
    color: var(--accent-fg);
    border-radius: 999px;
    padding: 0.05rem 0.4rem;
    font-size: 0.66rem;
    letter-spacing: 0.04em;
  }

  .entry {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .tap {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
    text-align: left;
    background: transparent;
    border-color: transparent;
    padding: 0.45rem 0.5rem;
    min-height: 40px;
  }

  .tap:hover {
    background: var(--surface);
    border-color: var(--line);
  }

  .hours {
    font-weight: 700;
    min-width: 2.2rem;
    flex: none;
  }

  .notes {
    color: var(--muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }

  .pending {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--accent);
    flex: none;
  }

  .del {
    font-size: 1.2rem;
    line-height: 1;
    min-height: 36px;
    padding: 0 0.6rem;
    color: var(--muted);
    flex: none;
  }

  .del:hover {
    color: var(--danger);
  }

  .confirm {
    flex: none;
  }

  .confirm button {
    min-height: 36px;
    padding: 0.2rem 0.6rem;
    font-size: 0.85rem;
  }

  .edit {
    flex: 1;
    display: grid;
    grid-template-columns: 5rem 1fr auto;
    gap: 0.4rem;
    align-items: center;
  }

  .edit input {
    min-height: 38px;
  }

  .edit .row button {
    min-height: 38px;
    padding: 0.2rem 0.7rem;
  }

  @media (max-width: 32rem) {
    .edit {
      grid-template-columns: 1fr;
    }
  }
</style>
